import type { CSSProperties } from 'react';
import { z } from 'zod';

export const BadgeOfferSchema = z.object({
  /**
   * `promo` is a regular marketing badge selected by admins, e.g. Hot Deal or Best Deal.
   * `system` is reserved for app-generated badges, currently the calculated SC extra badge.
   */
  type: z.enum(['system', 'promo']).optional(),
  label: z.string(),
  styles: z.object({
    stroke: z.object({
      width: z.string(),
      gradient: z.string(),
    }).optional(),
    cssStyles: z.custom<CSSProperties>(),
  }).optional(),
});

const BadgeOfferValueSchema = z
  .union([BadgeOfferSchema, z.array(BadgeOfferSchema)])
  .transform((value) => {
    const badges = Array.isArray(value) ? value : [value];

    return badges.map(badge => ({ ...badge, type: badge.type ?? 'promo' }));
  });

export const RelatedItemSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
  })
  .passthrough();

export const BUNDLE_OFFER_APPEARANCES = ['baseShop', 'promo', 'vip', 'gamificationShop'] as const;
export const BundleOfferAppearanceSchema = z.enum(BUNDLE_OFFER_APPEARANCES);

export const OfferPresentationSchema = z
  .object({
    appearance: BundleOfferAppearanceSchema.optional(),
    isVip: z.boolean().optional(),
    isPromo: z.boolean().optional(),
    isBaseShop: z.boolean().optional(),
    dailyBonusUpTo: z.number().optional(),
    badge: BadgeOfferValueSchema.nullish(),
  })
  .passthrough()
  .describe('OfferPresentationSchema');

export const ProductPresentationSchema = z
  .object({
    order: z.number().optional(),
    beforeSalePrice: z.number().optional(),
    isDefaultPromo: z.boolean().optional(),
    relatedProducts: z.array(RelatedItemSchema).optional(),
  })
  .passthrough()
  .describe('ProductPresentationSchema');

export const ProductAssetDtoSchema = z
  .object({
    id: z.string(),
    key: z.string().nullable().optional(),
    path: z.string(),
    size: z.number().optional(),
    mimeType: z.string().optional(),
    meta: z.any().optional(),
    appearance: z.string().nullable().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough()
  .describe('ProductAssetDtoSchema');

export const OfferDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    productId: z.string(),
    bsc: z.number(),
    gc: z.number(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    assets: z.array(ProductAssetDtoSchema).default([]),
    presentation: OfferPresentationSchema.nullish(),
    createdAt: z.string().optional(),
  })
  .passthrough()
  .describe('OfferDtoSchema');

export const ProductDtoSchema = z
  .object({
    id: z.string(),
    alias: z.string().nullable().optional(),
    name: z.string(),
    price: z.number(),
    sc: z.number(),
    presentation: ProductPresentationSchema.nullable().optional(),
    assets: z.array(ProductAssetDtoSchema).default([]).optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough()
  .describe('ProductDtoSchema');

export const UserBundleOfferDtoSchema = z
  .object({
    id: z.string(),
    userId: z.string().optional(),
    offerId: z.string().optional(),
    productId: z.string(),
    offer: OfferDtoSchema,
    product: ProductDtoSchema,
    count: z.number().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough()
  .describe('UserBundleOfferDtoSchema');

export const BundleOffersResponseSchema = z.array(UserBundleOfferDtoSchema);

export type BadgeOffer = z.infer<typeof BadgeOfferSchema>;
export type NormalizedBadgeOffer = BadgeOffer & { type: NonNullable<BadgeOffer['type']> };
export type BundleOfferAppearance = z.infer<typeof BundleOfferAppearanceSchema>;
export type OfferPresentation = z.infer<typeof OfferPresentationSchema>;
export type OfferPresentationInput = z.input<typeof OfferPresentationSchema>;
export type ProductAssetDto = z.infer<typeof ProductAssetDtoSchema>;
export type BundleOfferDto = z.infer<typeof UserBundleOfferDtoSchema>;

export const normalizeBadges = (badges?: BadgeOffer | BadgeOffer[] | null): NormalizedBadgeOffer[] => {
  if (!badges) {
    return [];
  }

  const badgeList = Array.isArray(badges) ? badges : [badges];

  return badgeList.map(badge => ({ ...badge, type: badge.type ?? 'promo' }));
};

/**
 * Resolves where an offer should be rendered in the Shop UI.
 *
 * The modern API contract uses `presentation.appearance` as the source of truth.
 * Older offers may still only have legacy placement booleans, so promo, VIP, and
 * base shop offers fall back to those flags. Gamification offers do not have a
 * supported legacy boolean in the storefront contract, so they must be tagged with
 * `appearance: 'gamificationShop'`.
 */
export const resolveBundleOfferAppearance = (
  presentation: OfferPresentationInput | null | undefined,
): BundleOfferAppearance | undefined => {
  if (!presentation) {
    return undefined;
  }

  if (presentation.appearance) {
    return presentation.appearance;
  }

  if (presentation.isPromo) {
    return 'promo';
  }

  if (presentation.isVip) {
    return 'vip';
  }

  if (presentation.isBaseShop) {
    return 'baseShop';
  }

  return undefined;
};
